import find from 'lodash/find'
import { get, isEmpty } from 'lodash'
import { default as deserialize, formatMeta } from 'app/utils/deserialize'
import { fetch } from 'app/utils/data'

const INITIAL_STATE = Object.freeze({
  list: [],
  item: null,
  subscriptionDiscounts: [],
  affiliateMeta: null
})

function getCanonicalFamilyStyle(icon) {
  const isClassicDuotoneIcon = icon.family === 'classic' && icon.style === 'duotone'

  return isClassicDuotoneIcon ? { family: 'duotone', style: 'solid' } : { family: icon.family, style: icon.style }
}

const iconIsIncludedInPlan = (state) => (icon, sku) => {
  if (icon.is_free) return true

  const { family, style } = getCanonicalFamilyStyle(icon)
  const plan = find(state.list, { sku: sku })

  if (!plan?.iconCollection?.familyStyles?.length) return false

  const isPaidPlan = plan.sku !== 'FA-FREE'
  const iconExists = plan.iconCollection.familyStyles.some((fs) => {
    return fs.family === family && fs.style === style
  })

  return isPaidPlan && iconExists
}

const canAccessIconSvg = (state) => (icon, plan, accessToProDownload) => {
  if (!icon) return false
  if (icon.is_free) return true
  if (!accessToProDownload) return false

  const activePlan = plan || { sku: 'FA-FREE' }
  const hasFreePlan = activePlan.sku === 'FA-FREE'

  if (hasFreePlan || !accessToProDownload) return false

  return iconIsIncludedInPlan(state)(icon, activePlan.sku)
}

const isProPlusIcon = (state) => (icon) => {
  const { family, style } = getCanonicalFamilyStyle(icon)

  // get all plans that contain this icon
  const plansWithIcon = state.list.filter((plan) => {
    if (!plan?.iconCollection?.familyStyles) return false

    return plan.iconCollection.familyStyles.some((fs) => fs.family === family && fs.style === style)
  })

  // return false if there are no plans that contain the icon
  if (plansWithIcon.length === 0) return false

  // return true if all plans that contain the icon are plus plans
  return plansWithIcon.every((plan) => plan.sku.toLowerCase().includes('plus'))
}

const getPlansIncluding = (state) => (icon, action, subscription, isCustomIcon) => {
  if (isEmpty(icon)) {
    return state.list.filter((plan) => plan.active && plan.sku !== 'FA-FREE' && plan.sku.startsWith('FA') && plan.price.amount > 0 && !!plan[action])
  }

  if (isCustomIcon) {
    const userCurrentPlanPrice = subscription?.plan?.price?.amount || 0

    return state.list.filter(
      (plan) =>
        plan.active &&
        plan.sku !== 'FA-FREE' &&
        plan.sku.startsWith('FA') &&
        plan.price.amount > 0 &&
        plan.price.amount > userCurrentPlanPrice &&
        !!plan[action]
    )
  }

  const { family, style } = getCanonicalFamilyStyle(icon)
  const iconCheck = iconIsIncludedInPlan(state)

  return state.list.filter((plan) => {
    return iconCheck(icon, plan.sku) && plan?.iconCollection?.familyStyles.some((fs) => fs.family === family && fs.style === style) && !!plan[action]
  })
}

const getPlansForUpgrade =
  (state) =>
  (options = {}) => {
    const { icon, userAction, subscription, isCustomIcon } = options
    const plans = getPlansIncluding(state)(icon, userAction, subscription, isCustomIcon) || []

    return Array.isArray(plans) ? plans : []
  }

const getNextUpgradePlans =
  (_state) =>
  (plans = [], subscription) => {
    if (!Array.isArray(plans)) return []

    const currentUserPlanAmount = subscription?.plan?.price?.amount || 0
    const currentPlanSku = subscription?.plan?.sku

    const result = plans
      .filter((plan) => {
        const isProPlusEnabled = !plan?.sku?.toLowerCase().includes('plus')
        const isCurrentPlanActive = !subscription?.endedAt && plan?.sku === currentPlanSku

        return (
          plan?.price?.amount >= currentUserPlanAmount &&
          plan?.sku !== 'FA-FREE' &&
          plan.sku.startsWith('FA') &&
          isProPlusEnabled &&
          !isCurrentPlanActive &&
          plan.active &&
          plan.price.amount > 0
        )
      })
      .sort((a, b) => a.price?.amount - b.price?.amount)
      .map((plan) => plan.sku)
      .slice(0, 3)

    return result
  }

const skusForFilteredPlans =
  (state) =>
  (options = {}) => {
    const plans = getPlansForUpgrade(state)(options)
    const upgradePlans = getNextUpgradePlans(state)(plans, options.subscription)

    return upgradePlans
  }

export default {
  namespaced: true,

  state: { ...INITIAL_STATE },

  getters: {
    iconIsIncludedInPlan,
    isProPlusIcon,
    getPlansIncluding,
    getPlansForUpgrade,
    getNextUpgradePlans,
    skusForFilteredPlans,
    canAccessIconSvg,

    currentPlans: (state) => state.list.filter((plan) => plan.active),

    allPlans: (state) => state.list,

    currentPlan: (state) => state.item,

    getPlan: (state) => (sku) => {
      return find(state.list, { sku: sku })
    },

    getPlanBySlug: (state) => (slug) => {
      return find(state.list, { slug: slug })
    },

    affiliateMeta: (state) => state.affiliateMeta,

    discount: (state) => state.discount,

    subscriptionDiscounts: (state) => state.subscriptionDiscounts
  },

  mutations: {
    plansFetched(state, { plans, affiliateMeta }) {
      state.list = plans
      state.affiliateMeta = affiliateMeta
    },

    planFetched(state, { plan }) {
      state.item = plan
    },

    discountsFetched(state, { discounts }) {
      state.subscriptionDiscounts = discounts
    },

    resetState(state) {
      state = { ...INITIAL_STATE }
    }
  },

  actions: {
    async fetchPlans({ commit }) {
      const response = await fetch({ url: `/api/plans` })

      const plans = await deserialize(response.data)
      const affiliateMeta = formatMeta(get(response.data, ['meta', 'affiliate']))

      commit('plansFetched', { plans, affiliateMeta })

      return plans
    },

    async fetchPlan({ commit }, sku) {
      const response = await fetch({ url: `/api/plans/${sku}` })

      const plan = await deserialize(response.data)

      commit('planFetched', { plan })

      return plan
    },

    async fetchDiscounts({ commit }, couponIds) {
      try {
        const response = await fetch({ url: `/api/discounts`, params: {couponIds: couponIds} })

        const discounts = await deserialize(response.data)

        commit('discountsFetched', { discounts: discounts })

        return discounts
      } catch (error) {
        return error
      }
    }
  }
}
